import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Calculator, Atom, FlaskConical, Languages, Code2,
  PenTool, BookOpen, Film, ArrowRight, Sparkles, Crown,
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

/**
 * FeaturedLessons — compact, fast lesson-category grid placed directly under Hero.
 * Focused on conversion: every card → /tutors filtered by subject.
 *
 * "Creative Writing" is the hero card, badged "Path to Authorship" and showing
 * a small "Master writing → Design with TD → Animate with TVD" bridge — making
 * it the explicit on-ramp into the wider Trust Creator Suite.
 */
const FeaturedLessons = ({ language: _legacyLang, isDark = false }) => {
  const navigate = useNavigate();
  const { language, localize } = useLanguage();
  const isAr = language === 'ar';
  // `t(ar, en)` keeps the original signature but routes through `localize` so
  // Spanish / Hindi / Urdu / French / Russian get the runtime auto-translation.
  const t = (ar, en) => localize({ ar, en });

  const lessons = [
    {
      key: 'creative-writing',
      icon: PenTool,
      titleAr: 'الكتابة الإبداعية',
      titleEn: 'Creative Writing',
      subtitleAr: 'من الفكرة إلى الكتاب المنشور',
      subtitleEn: 'From idea to published book',
      subject: 'الكتابة الإبداعية',
      featured: true,
    },
    {
      key: 'math',
      icon: Calculator,
      titleAr: 'الرياضيات',
      titleEn: 'Mathematics',
      subtitleAr: 'جبر، حساب، هندسة',
      subtitleEn: 'Algebra, calculus, geometry',
      subject: 'الرياضيات',
    },
    {
      key: 'physics',
      icon: Atom,
      titleAr: 'الفيزياء',
      titleEn: 'Physics',
      subtitleAr: 'ميكانيكا، كهرومغناطيسية',
      subtitleEn: 'Mechanics, electromagnetism',
      subject: 'الفيزياء',
    },
    {
      key: 'chemistry',
      icon: FlaskConical,
      titleAr: 'الكيمياء',
      titleEn: 'Chemistry',
      subtitleAr: 'عضوية، تحليلية، حيوية',
      subtitleEn: 'Organic, analytical, biochem',
      subject: 'الكيمياء',
    },
    {
      key: 'english',
      icon: Languages,
      titleAr: 'اللغة الإنجليزية',
      titleEn: 'English & IELTS',
      subtitleAr: 'محادثة، قواعد، اختبارات',
      subtitleEn: 'Speaking, grammar, exams',
      subject: 'اللغة الإنجليزية',
    },
    {
      key: 'coding',
      icon: Code2,
      titleAr: 'البرمجة',
      titleEn: 'Coding & CS',
      subtitleAr: 'Python, JavaScript, AI',
      subtitleEn: 'Python, JavaScript, AI',
      subject: 'الحاسوب',
    },
  ];

  return (
    <section
      className={`${isDark ? 'bg-[#0d1117]' : 'bg-white'} py-10 sm:py-14 border-y border-slate-200 dark:border-white/5`}
      dir={isAr ? 'rtl' : 'ltr'}
      data-testid="featured-lessons"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex items-end justify-between gap-4 mb-6 sm:mb-8 flex-wrap">
          <div>
            <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.18em] text-indigo-600 dark:text-indigo-400 mb-1.5">
              {t('الدروس المميزة', 'Featured Lessons')}
            </p>
            <h2 className={`text-2xl sm:text-3xl font-black tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {t('ابدأ الدرس الآن', 'Start a lesson now')}
            </h2>
          </div>
          <button
            onClick={() => navigate('/tutors')}
            className="text-xs sm:text-sm font-bold text-indigo-600 dark:text-indigo-300 hover:text-indigo-500 inline-flex items-center gap-1 transition-colors"
            data-testid="featured-lessons-view-all"
          >
            {t('عرض الكل', 'View all')}
            <ArrowRight className={`w-3.5 h-3.5 ${isAr ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Grid: Featured card spans 2 cols on lg */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {lessons.map((lesson) => {
            const Icon = lesson.icon;
            const isFeatured = lesson.featured;
            return (
              <motion.button
                key={lesson.key}
                type="button"
                onClick={() => navigate(`/tutors?subject=${encodeURIComponent(lesson.subject)}`)}
                whileHover={{ y: -2 }}
                transition={{ duration: 0.18 }}
                className={`relative text-${isAr ? 'right' : 'left'} rounded-2xl p-4 sm:p-5 border transition-colors group overflow-hidden ${
                  isFeatured
                    ? `col-span-2 ${isDark ? 'bg-gradient-to-br from-indigo-950 via-violet-950 to-fuchsia-950 border-indigo-500/30' : 'bg-gradient-to-br from-indigo-50 via-violet-50 to-fuchsia-50 border-indigo-200'} hover:shadow-lg`
                    : isDark
                      ? 'bg-[#161b22] border-[#30363d] hover:border-indigo-500/40'
                      : 'bg-white border-slate-200 hover:border-indigo-300 hover:shadow-md'
                }`}
                data-testid={`lesson-card-${lesson.key}`}
              >
                {/* Featured "Path to Authorship" badge */}
                {isFeatured && (
                  <div className="absolute top-3 end-3 inline-flex items-center gap-1 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-[9px] sm:text-[10px] font-black uppercase tracking-wide px-2 py-1 rounded-full shadow-md" data-testid="path-to-authorship-badge">
                    <Crown className="w-2.5 h-2.5" />
                    {t('طريق التأليف', 'Path to Authorship')}
                  </div>
                )}

                <div className={`inline-flex items-center justify-center w-10 h-10 sm:w-11 sm:h-11 rounded-xl mb-3 ${
                  isFeatured
                    ? 'bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-md'
                    : isDark
                      ? 'bg-indigo-500/10 text-indigo-300'
                      : 'bg-indigo-50 text-indigo-600'
                }`}>
                  <Icon className="w-5 h-5" />
                </div>

                <h3 className={`text-sm sm:text-base font-bold mb-0.5 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {localize({ ar: lesson.titleAr, en: lesson.titleEn })}
                </h3>
                <p className={`text-[11px] sm:text-xs leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  {localize({ ar: lesson.subtitleAr, en: lesson.subtitleEn })}
                </p>

                {/* Creator Bridge hint — only on Creative Writing */}
                {isFeatured && (
                  <div className="mt-3 pt-3 border-t border-indigo-200/40 dark:border-indigo-800/40" data-testid="creator-bridge-hint">
                    <p className={`text-[10px] sm:text-[11px] font-semibold leading-relaxed flex items-center gap-1.5 flex-wrap ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                      <Sparkles className="w-3 h-3 text-amber-500" />
                      <span className="inline-flex items-center gap-1">
                        <PenTool className="w-3 h-3 text-indigo-500" />
                        {t('أتقن الكتابة', 'Master writing')}
                      </span>
                      <ArrowRight className={`w-3 h-3 text-slate-400 ${isAr ? 'rotate-180' : ''}`} />
                      <span className="inline-flex items-center gap-1">
                        <BookOpen className="w-3 h-3 text-emerald-500" />
                        {t('صمّم مع TD', 'Design with TD')}
                      </span>
                      <ArrowRight className={`w-3 h-3 text-slate-400 ${isAr ? 'rotate-180' : ''}`} />
                      <span className="inline-flex items-center gap-1">
                        <Film className="w-3 h-3 text-amber-500" />
                        {t('حرّكها مع TVD', 'Animate with TVD')}
                      </span>
                    </p>
                  </div>
                )}
              </motion.button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeaturedLessons;
